import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { RealEstateChatMessage, RealEstateChatThread } from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_REAL_ESTATE_CHAT_MESSAGES,
  COLLECTION_REAL_ESTATE_CHAT_THREADS,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { newId } from '../utils/id';

const FIRESTORE_BATCH_LIMIT = 500;

export type RealEstateChatThreadsState = {
  threads: RealEstateChatThread[];
  messages: RealEstateChatMessage[];
  threadsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  createThread: (title?: string) => Promise<RealEstateChatThread>;
  deleteThread: (threadId: string) => Promise<void>;
};

function docToMessage(d: QueryDocumentSnapshot): RealEstateChatMessage {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    propertyId: typeof x.propertyId === 'string' ? x.propertyId : '',
    threadId: typeof x.threadId === 'string' ? x.threadId : '',
    role: x.role === 'assistant' ? 'assistant' : 'user',
    content: typeof x.content === 'string' ? x.content : '',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

function docToThread(d: QueryDocumentSnapshot): RealEstateChatThread {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    propertyId: typeof x.propertyId === 'string' ? x.propertyId : '',
    title: typeof x.title === 'string' && x.title.trim() ? x.title.trim() : 'New chat',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

export function sortRealEstateChatChronological(
  messages: RealEstateChatMessage[]
): RealEstateChatMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function sortRealEstateChatThreadsRecentFirst(
  threads: RealEstateChatThread[]
): RealEstateChatThread[] {
  return [...threads].sort((a, b) => {
    const byUpdated = b.updatedAt.localeCompare(a.updatedAt);
    if (byUpdated !== 0) return byUpdated;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function useRealEstateChatThreads(
  userId: string,
  propertyId: string,
  activeThreadId: string | null
): RealEstateChatThreadsState {
  const [storedThreads, setStoredThreads] = useState<RealEstateChatThread[]>([]);
  const [allMessages, setAllMessages] = useState<RealEstateChatMessage[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(Boolean(userId && propertyId));
  const [messagesLoading, setMessagesLoading] = useState(Boolean(userId && propertyId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !propertyId) {
      setStoredThreads([]);
      setThreadsLoading(false);
      setError(null);
      return;
    }

    setThreadsLoading(true);

    const threadsQuery = query(
      collection(db, COLLECTION_REAL_ESTATE_CHAT_THREADS),
      where('userId', '==', userId),
      where('propertyId', '==', propertyId)
    );

    const unsubscribe = onSnapshot(
      threadsQuery,
      (snap) => {
        setStoredThreads(snap.docs.map(docToThread));
        setThreadsLoading(false);
      },
      (e) => {
        setError(e.message);
        setThreadsLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, propertyId]);

  useEffect(() => {
    if (!userId || !propertyId) {
      setAllMessages([]);
      setMessagesLoading(false);
      return;
    }

    setMessagesLoading(true);

    const messagesQuery = query(
      collection(db, COLLECTION_REAL_ESTATE_CHAT_MESSAGES),
      where('userId', '==', userId),
      where('propertyId', '==', propertyId)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snap) => {
        setAllMessages(snap.docs.map(docToMessage));
        setMessagesLoading(false);
      },
      (e) => {
        setError(e.message);
        setMessagesLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, propertyId]);

  const threads = useMemo(
    () => sortRealEstateChatThreadsRecentFirst(storedThreads),
    [storedThreads]
  );

  const messages = useMemo(
    () =>
      sortRealEstateChatChronological(
        activeThreadId ? allMessages.filter((message) => message.threadId === activeThreadId) : []
      ),
    [activeThreadId, allMessages]
  );

  const createThread = useCallback(
    async (title = 'New chat'): Promise<RealEstateChatThread> => {
      if (!userId) {
        throw new Error('Sign in to start a chat');
      }

      const now = new Date().toISOString();
      const thread: RealEstateChatThread = {
        id: newId(),
        userId,
        propertyId,
        title: title.trim() || 'New chat',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(
        doc(db, COLLECTION_REAL_ESTATE_CHAT_THREADS, ownedItemDocId(userId, thread.id)),
        thread
      );

      return thread;
    },
    [propertyId, userId]
  );

  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      if (!userId) {
        throw new Error('Sign in to delete a chat');
      }

      const messagesQuery = query(
        collection(db, COLLECTION_REAL_ESTATE_CHAT_MESSAGES),
        where('userId', '==', userId),
        where('propertyId', '==', propertyId),
        where('threadId', '==', threadId)
      );

      const messageSnap = await getDocs(messagesQuery);
      const refsToDelete: DocumentReference[] = [
        ...messageSnap.docs.map((messageDoc) => messageDoc.ref),
        doc(db, COLLECTION_REAL_ESTATE_CHAT_THREADS, ownedItemDocId(userId, threadId)),
      ];

      for (let i = 0; i < refsToDelete.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        for (const ref of refsToDelete.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
          batch.delete(ref);
        }
        await batch.commit();
      }
    },
    [propertyId, userId]
  );

  return useMemo(
    () => ({
      threads,
      messages,
      threadsLoading,
      messagesLoading,
      error,
      createThread,
      deleteThread,
    }),
    [threads, messages, threadsLoading, messagesLoading, error, createThread, deleteThread]
  );
}
